// ── All Participation Received Payments — global list ──
// Payments recorded against participant scheduled payments, across every loan:
// the same records as the Participant Payments table on a loan's Participant
// Pmts tab, with the loan and account beside each. View, edit and delete open
// the same form.
import { useState, useEffect } from "react";
import { localFilter, exportCSV, fmt, fmtDate } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, SearchBar, Btn, Card, STh, Th, Td, TdNum, Icon, Pagination } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const AllParticipantPmtsPage = () => {
  const { api, setModal, role, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const load = () => { setLoading(true); api.get("getParticipantPmts")
    .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
    .catch(e => { setErr(e.message); setLoading(false); }); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = localFilter(rows, q, ["loannumber", "companyname", "paymentdate", "feetype", "othertype"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, "paymentdate", "desc");
  const { paginated: displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);
  const H = (label, k, numeric) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric={numeric} />;
  const modalFor = (type, p, extra) => setModal({ type, rec: { ...p, id: p.id, loanid: p.loanid },
    loanid: p.loanid, participantscheduledpmtid: p.participantscheduledpmtid, ...extra });

  if (loading) return <Spinner />; if (err) return <ErrorMsg msg={err} onRetry={load} />;
  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v => { setQ(v); reset(); }} placeholder="Search participation payments..." />,
      <Btn key="ex" onClick={() => exportCSV(sorted, ["loannumber", "companyname", "paymentdate", "principalamount", "interestamount",
        "feetype", "feeamount", "othertype", "otheramount", "totalpayment"], "participation_received_pmts.csv")}>Export CSV</Btn>
    ]} />
    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          {H("Loan #", "loannumber")}{H("Account", "companyname")}{H("Payment Date", "paymentdate")}
          {H("Principal", "principalamount", true)}{H("Interest", "interestamount", true)}{H("Fee Type", "feetype")}
          {H("Fee", "feeamount", true)}{H("Other Type", "othertype")}{H("Other", "otheramount", true)}
          {H("Total", "totalpayment", true)}<Th></Th>
        </tr></thead>
        <tbody>{displayed.map(p => (
          <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => modalFor("participantpmt-view", p)}
              onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{p.loannumber || "—"}</span></Td>
            <Td style={{ fontWeight: 500 }}>{p.companyname || "—"}</Td>
            <Td>{fmtDate(p.paymentdate)}</Td>
            <TdNum>{fmt(p.principalamount || 0)}</TdNum>
            <TdNum>{fmt(p.interestamount || 0)}</TdNum>
            <Td>{p.feetype || "—"}</Td>
            <TdNum>{fmt(p.feeamount || 0)}</TdNum>
            <Td>{p.othertype || "—"}</Td>
            <TdNum>{fmt(p.otheramount || 0)}</TdNum>
            <TdNum style={{ fontWeight: 500 }}>{fmt(p.totalpayment || 0)}</TdNum>
            <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={e => e.stopPropagation()}>
              <Btn onClick={() => modalFor("participantpmt-view", p)}><Icon type="view" /></Btn>
              {role.canEdit && <Btn onClick={() => modalFor("participantpmt-edit", p, { onDone: load })}><Icon type="edit" /></Btn>}
              {role.canDelete && <Btn variant="danger" onClick={() => setConfirm({ msg: "Delete this participant payment?",
                onConfirm: () => delRec("ParticipantPmts", p.id, "Deleted participant payment", load) })}><Icon type="delete" /></Btn>}
            </div></Td>
          </tr>))}</tbody>
      </table>
      {!rows.length && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: 12 }}>No participation payments recorded.</div>}
      <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
    </Card>
  </>;
};
